import { StandardEvents } from '@shopify/events';

/**
 * ASamplesX quick-add state mirror.
 *
 * The ASamplesX component spec asks for five card states driven from one
 * `data-state` attribute: ready, loading, added, error, sold-out. Horizon already
 * models every one of those, just spread across three different signals - the
 * button's `disabled` attribute, the `data-added` attribute set by
 * `product-form.js`, and the `shopify:cart:error` standard event.
 *
 * This module only observes those signals and reflects them onto
 * `<quick-add-component data-state>`. It is deliberately read-only: it never
 * calls the Cart API, never intercepts a submit, and never changes what Horizon
 * does. Deleting this file would cost the styling hook and nothing else.
 */

const ADDED_HOLD_MS = 1900;
const ERROR_HOLD_MS = 4000;

/** @type {WeakMap<Element, number>} */
const timers = new WeakMap();

/**
 * @param {Element} component
 * @param {string} state
 * @param {number} [revertAfter]
 */
function setState(component, state, revertAfter) {
  if (!(component instanceof HTMLElement)) return;

  const existing = timers.get(component);
  if (existing) {
    clearTimeout(existing);
    timers.delete(component);
  }

  component.dataset.state = state;

  if (revertAfter) {
    const id = window.setTimeout(() => {
      timers.delete(component);
      setState(component, resolveRestingState(component));
    }, revertAfter);
    timers.set(component, id);
  }
}

/**
 * The state a card returns to once transient feedback clears.
 * @param {HTMLElement} component
 * @returns {string}
 */
function resolveRestingState(component) {
  const button = component.querySelector('.quick-add__button');
  if (button instanceof HTMLButtonElement && button.disabled) return 'sold-out';
  return 'ready';
}

/** @param {Element} component */
function initComponent(component) {
  if (!(component instanceof HTMLElement) || component.dataset.asxStateBound === 'true') return;
  component.dataset.asxStateBound = 'true';

  setState(component, resolveRestingState(component));

  // `product-form.js` sets `data-added` on the button and removes it after its own
  // hold. Watch the attribute rather than racing it with a second timer.
  const observer = new MutationObserver((records) => {
    for (const record of records) {
      const target = record.target;
      if (!(target instanceof HTMLElement)) continue;

      if (record.attributeName === 'data-added') {
        if (target.hasAttribute('data-added')) setState(component, 'added', ADDED_HOLD_MS);
        continue;
      }

      if (record.attributeName === 'disabled' && component.dataset.state !== 'added') {
        setState(component, resolveRestingState(component));
      }
    }
  });

  observer.observe(component, {
    subtree: true,
    attributes: true,
    attributeFilter: ['data-added', 'disabled'],
  });
}

function initAll(root = document) {
  for (const component of root.querySelectorAll('quick-add-component')) initComponent(component);
}

// A press on the add button puts the card into `loading` until the cart settles.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.quick-add__button--add, .add-to-cart-button');
    if (!button) return;
    const component = button.closest('quick-add-component');
    if (!component || (button instanceof HTMLButtonElement && button.disabled)) return;
    setState(component, 'loading');
  },
  { capture: true }
);

// Success and failure both arrive as standard cart events, so the resting state is
// restored even when the add is rejected server-side.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const component = /** @type {Element | null} */ (event.target)?.closest?.('quick-add-component');
  const scope = component ? [component] : document.querySelectorAll('quick-add-component[data-state="loading"]');
  for (const el of scope) {
    if (el instanceof HTMLElement && el.dataset.state === 'loading') {
      setState(el, resolveRestingState(el));
    }
  }
});

document.addEventListener(StandardEvents.cartError, () => {
  for (const el of document.querySelectorAll('quick-add-component[data-state="loading"]')) {
    setState(el, 'error', ERROR_HOLD_MS);
  }
});

// Cards arrive and leave as facets, pagination and section rendering morph the
// grid, so keep watching rather than binding once.
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches?.('quick-add-component')) initComponent(node);
      else initAll(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

initAll();
